import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Pencil, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useStores, useDeleteStore } from "@/hooks/use-stores";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function StoresSection() {
  const { data: stores, isLoading } = useStores();
  const deleteStore = useDeleteStore();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteStore.mutateAsync(deleteTarget.id);
      toast.success("Trgovina obrisana");
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e.message || "Greška pri brisanju");
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end">
        <Button asChild size="sm">
          <Link to="/stores/new">Dodaj trgovinu</Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : stores && stores.length > 0 ? (
        <div className="ring-1 ring-foreground/10">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="p-2 text-left font-medium">Naziv</th>
                <th className="p-2 text-left font-medium">URL</th>
                <th className="p-2 text-right font-medium">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {stores.map((store: any) => (
                <tr key={store.id} className="border-b hover:bg-muted/50 transition-colors">
                  <td className="p-2 font-medium">{store.name}</td>
                  <td className="p-2 text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate">{store.url}</span>
                      <a href={store.url} target="_blank" rel="noopener noreferrer" className="shrink-0 hover:text-foreground">
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" asChild>
                        <Link to="/stores/$storeId/edit" params={{ storeId: store.id }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget({ id: store.id, name: store.name })}
                        disabled={deleteStore.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground py-8 text-center">Nema dodanih trgovina</p>
      )}

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Obriši trgovinu</DialogTitle>
            <DialogDescription>
              Jeste li sigurni da želite obrisati trgovinu "{deleteTarget?.name}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Otkaži</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteStore.isPending}>Obriši</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
